<template>
  <v-container fluid class="order-create-page pa-0">
    <v-alert
      v-if="selectedCustomer && selectedCustomer.alert"
      border="left"
      type="error"
      transition="slide-y-transition"
      dismissible
      class="mb-3"
      v-model="selectedCustomer.alert"
      color="red"
    >
      <strong>{{ selectedCustomer.note }}</strong>
    </v-alert>

    <div class="floating-order-bar" :class="{ show: showFloatingBar }">
      <div>
        <strong>Edit Order #{{ Order.localId }}</strong>
        <span v-if="selectedCustomer && selectedCustomer.name">
          · {{ selectedCustomer.name }}
        </span>
      </div>

      <div class="floating-actions">
        <v-btn x-small rounded outlined @click.stop.prevent="cancel">
          Cancel
        </v-btn>

        <v-btn
          x-small
          rounded
          color="primary"
          dark
          :disabled="Order.OrderDetails.length === 0"
          @click="saveOrder"
        >
          {{ invoiceTotal | currency }} Update
        </v-btn>
      </div>
    </div>

    <v-card outlined class="order-create-card">
      <div class="order-create-header">
        <div>
          <div class="order-create-title">
            Edit In-House Order #{{ Order.localId }}

            <v-chip
              v-if="isDraftOrder"
              x-small
              class="ml-2 font-weight-bold"
              color="orange lighten-5"
              text-color="orange darken-3"
            >
              Draft
            </v-chip>

            <v-chip
              v-if="Order.source"
              x-small
              class="ml-2 font-weight-bold"
              color="blue lighten-5"
              text-color="blue darken-3"
            >
              {{ sourceLabel }}
            </v-chip>

            <v-chip
              v-if="
                selectedCustomer &&
                selectedCustomer.PriceTier &&
                currentBusiness &&
                currentBusiness.metadata &&
                !currentBusiness.metadata.isSingleCustomerPricing
              "
              x-small
              class="ml-2 font-weight-bold"
              color="red"
              text-color="white"
            >
              {{ selectedCustomer.PriceTier.name }}
            </v-chip>
          </div>

          <div class="order-create-subtitle">
            <strong v-if="isRepairNestOrder">Order from RepairNest<span v-if="marketOrderNumber"> · {{ marketOrderNumber }}</span></strong><br v-if="isRepairNestOrder">
            <span v-if="isRepairNestOrder && retailerName">{{ retailerName }}</span><br v-if="isRepairNestOrder && retailerName">
            Update the order here. Draft orders must be submitted before they
            can be converted to invoice.
          </div>
        </div>

        <div class="order-create-actions">
          <v-btn
            rounded
            outlined
            :small="$vuetify.breakpoint.smAndDown"
            @click.stop.prevent="cancel"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            rounded
            dark
            :small="$vuetify.breakpoint.smAndDown"
            :disabled="Order.OrderDetails.length === 0"
            @click="saveOrder"
          >
            <v-icon left small>mdi-content-save-outline</v-icon>
            {{ invoiceTotal | currency }} Update
          </v-btn>

          <v-btn
            v-if="isDraftOrder"
            color="green darken-1"
            rounded
            dark
            :small="$vuetify.breakpoint.smAndDown"
            :disabled="Order.OrderDetails.length === 0"
            @click="submitOrder"
          >
            <v-icon left small>mdi-send-check-outline</v-icon>
            Submit
          </v-btn>
        </div>
      </div>

      <v-card-text class="pa-4">
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <div class="field-label">Customer</div>
            <AutoComplete
              :initValue="initValue"
              v-model="selectedCustomer"
              :errorMessages="selectCustomerError"
            />
          </v-col>

          <v-col cols="12" md="3">
            <div class="field-label">Sales Rep</div>
            <SalesMan v-model="selectedUser" :initValue="initUser" />
          </v-col>

          <v-col cols="12" md="2">
            <div class="field-label">Date</div>
            <DateSelector label="Date" v-model="Order.date" />
          </v-col>

          <v-col
            cols="12"
            md="3"
            v-if="
              !(
                currentBusiness &&
                currentBusiness.metadata &&
                currentBusiness.metadata.isSingleCustomerPricing
              )
            "
          >
            <div class="field-label">Price Tier</div>
            <v-select
              :items="tiers"
              item-text="name"
              item-value="id"
              v-model="Order.PriceTierId"
              label="Select Price Tier"
              outlined
              dense
              hide-details
              clearable
              @change="onPriceTierChange"
            />
          </v-col>

          <v-col cols="12">
            <div class="note-row">
              <v-dialog v-model="orderNote" width="500">
                <template v-slot:activator="{ on }">
                  <v-btn small rounded outlined v-on="on">
                    <v-icon left small>mdi-note-text-outline</v-icon>
                    Order Note
                  </v-btn>
                </template>

                <v-card class="note-dialog-card">
                  <div class="dialog-header">
                    <div>
                      <div class="dialog-title">Order Note</div>
                      <div class="dialog-subtitle">
                        Add internal note for this order.
                      </div>
                    </div>

                    <v-btn icon small @click="orderNote = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>

                  <v-card-text class="pa-4">
                    <v-textarea
                      outlined
                      dense
                      hide-details
                      auto-grow
                      rows="4"
                      v-model="Order.note"
                      label="Order Note"
                    />
                  </v-card-text>

                  <v-divider />

                  <v-card-actions class="dialog-actions">
                    <v-spacer />
                    <v-btn
                      color="primary"
                      rounded
                      dark
                      @click="orderNote = false"
                    >
                      Done
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <div v-if="Order.note" class="note-preview">
                {{ Order.note }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div v-if="selectedCustomer && selectedCustomer.id" class="mt-3">
      <ProductSearchBar
        :price-tier-id="Order.PriceTierId"
        :selectedCustomer="selectedCustomer"
        @submit="addRow"
      />
    </div>

    <v-card
      v-if="selectedCustomer && selectedCustomer.id"
      outlined
      class="order-cart-card mt-3"
    >
      <div class="cart-header">
        <div>
          <div class="cart-title">Order Cart</div>
          <div class="cart-subtitle">
            {{ Order.OrderDetails.length }} row{{
              Order.OrderDetails.length === 1 ? "" : "s"
            }}
            · {{ totalQty }} item{{ totalQty === 1 ? "" : "s" }}
          </div>
        </div>

        <div class="cart-total">
          {{ invoiceTotal | currency }}
        </div>
      </div>

      <v-card-text class="pa-4">
        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          hide-default-footer
          :items-per-page="1000"
          no-data-text="No item on this order yet"
          :items="Order.OrderDetails"
          class="order-cart-table"
        >
          <template v-slot:item="{ item, index }">
            <tr>
              <td class="image-cell">
                <v-avatar tile size="46" class="cart-image-avatar">
                  <v-img
                    v-if="getItemImageUrl(item)"
                    contain
                    :src="getItemImageUrl(item)"
                    alt="Product image"
                  />

                  <v-img
                    v-else
                    src="@/assets/images/photoComingSoon.png"
                    contain
                    alt="No image"
                  />
                </v-avatar>
              </td>

              <td>
                <div class="cart-name-row">
                  <v-btn icon small @click="deleteItem(index)">
                    <v-icon color="red">mdi-delete-outline</v-icon>
                  </v-btn>

                  <v-text-field
                    v-model="item.name"
                    outlined
                    hide-details
                    :disabled="lockProductName"
                    @focus="$event.target.select()"
                    dense
                  />
                </div>
              </td>

              <td
                v-if="
                  currentBusiness &&
                  currentBusiness.metadata &&
                  currentBusiness.metadata.boxCountOnInventory
                "
              >
                <div class="text-center">{{ item.unitCountInBox }}</div>
              </td>

              <td class="text-right qty-cell">
                <v-text-field
                  v-model="item.qty"
                  outlined
                  @blur="checkPrice(item)"
                  class="centered-input"
                  hide-details
                  dense
                  @focus="$event.target.select()"
                />
              </td>

              <td class="text-right price-cell">
                <v-text-field
                  v-model="item.price"
                  outlined
                  class="al-r"
                  hide-details
                  @blur="checkPrice(item)"
                  @focus="$event.target.select()"
                  dense
                />
              </td>

              <td class="text-right font-weight-bold">
                {{ item.total | currency }}
              </td>
            </tr>
            <tr
              v-if="shouldShowPriceSuggestionRow(item)"
              class="price-change-row"
            >
              <td :colspan="headers.length">
                <div
                  class="price-change-edit-alert"
                  :class="{
                    restored:
                      item.metadata.priceSuggestionStatus === 'restored',
                  }"
                >
                  <v-icon
                    small
                    :color="
                      item.metadata.priceSuggestionStatus === 'restored'
                        ? 'green darken-2'
                        : 'deep-orange darken-2'
                    "
                    class="mr-2"
                  >
                    {{
                      item.metadata.priceSuggestionStatus === "restored"
                        ? "mdi-check-circle-outline"
                        : "mdi-tag-edit-outline"
                    }}
                  </v-icon>

                  <div class="price-change-content">
                    <div
                      class="font-weight-bold"
                      v-if="item.metadata.priceSuggestionStatus === 'restored'"
                    >
                      Original price has been restored.
                    </div>

                    <div class="font-weight-bold" v-else>
                      Sales Rep Price Suggestion
                    </div>

                    <div
                      v-if="item.metadata.priceSuggestionStatus === 'restored'"
                    >
                      Current Price:
                      <strong>{{ item.price | currency }}</strong>

                      · Suggested Price:
                      <strong>{{
                        item.metadata.suggestedPrice | currency
                      }}</strong>

                      <span v-if="getSuggestedBy(item)">
                        · Suggested by
                        <strong>{{ getSuggestedBy(item) }}</strong>
                      </span>
                    </div>

                    <div v-else>
                      Original Price:
                      <strong>{{ getOriginalPrice(item) | currency }}</strong>

                      · Current Price:
                      <strong>{{ item.price | currency }}</strong>

                      <span v-if="getSuggestedBy(item)">
                        · Changed by
                        <strong>{{ getSuggestedBy(item) }}</strong>
                      </span>
                    </div>
                  </div>

                  <v-spacer></v-spacer>

                  <v-btn
                    v-if="item.metadata.priceSuggestionStatus !== 'restored'"
                    small
                    text
                    color="deep-orange darken-2"
                    @click.stop="restoreOriginalPrice(item)"
                  >
                    Restore Original
                  </v-btn>

                  <v-btn
                    v-else
                    small
                    text
                    color="green darken-2"
                    @click.stop="applySuggestedPrice(item)"
                  >
                    Apply Suggested Price
                  </v-btn>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card outlined tile v-if="totalQty > 0">
        <v-card-text>
          <div class="text-left">
            <span class="font-weight-medium">{{ totalQty }}</span> Total Item
          </div>
        </v-card-text>
      </v-card>
    </v-card>
  </v-container>
</template>

<script>
import AutoComplete from "@/modules/Customer/components/AutoComplete";
import customerService from "@/modules/Customer/service";
import productService from "@/modules/Product/service";
import orderService from "./service";
import SalesMan from "@/modules/User/components/AutoComplete.vue";
import ProductSearchBar from "@/modules/Order/components/InHouse/ProductSearchBar.vue";
import priceTierService from "@/modules/Product/PriceTier/service.js";
import DateSelector from "@/components/DateSelector";
import { mapGetters } from "vuex";

const { validationMixin } = require("vuelidate");
const { required, minValue } = require("vuelidate/lib/validators");

export default {
  name: "edit-order",

  components: {
    DateSelector,
    SalesMan,
    ProductSearchBar,
    AutoComplete,
  },

  mixins: [validationMixin],

  data() {
    return {
      showFloatingBar: false,
      initUser: null,
      selectedUser: null,
      initValue: null,
      orderNote: false,
      selectedCustomer: null,
      Customer: [],
      Products: [],
      loading: true,
      submitting: false,
      Order: {
        date: this.$moment().toISOString(),
        note: null,
        CustomerId: null,
        SalesRepUserId: null,
        PriceTierId: null,
        OrderDetails: [],
      },
      headers: [],
      tiers: [],
    };
  },

  validations: {
    Order: {
      CustomerId: {
        required,
        minValue: minValue(1),
      },
    },
  },

  computed: {
    ...mapGetters("global", ["currentBusiness"]),

    isDraftOrder() {
      return this.Order && this.Order.status === "draft";
    },

    sourceLabel() {
      if (this.isRepairNestOrder) return "RepairNest";
      if (this.Order.source === "catalog") return "Catalog";
      if (this.Order.source === "remote") return "Remote";
      if (this.Order.source === "inHouse") return "In House";
      if (this.Order.source === "choco") return "Choco";
      return this.Order.source;
    },

    isRepairNestOrder() {
      return !!(this.Order && (this.Order.source === "repairnest" || this.Order.sourceLabel === "RepairNest" || this.Order.displayOrderNumber || (this.Order.metadata && this.Order.metadata.source === "repairnest")));
    },

    marketOrderNumber() {
      return this.Order && (this.Order.displayOrderNumber || this.Order.marketOrderNumber);
    },

    retailerName() {
      const retailer = this.Order && (this.Order.retailer || (this.Order.metadata && this.Order.metadata.retailer));
      if (!retailer) return "";
      return [retailer.businessName || retailer.name, retailer.locationName].filter(Boolean).join(" · ");
    },

    lockProductName() {
      if (!this.currentBusiness || !this.currentBusiness.metadata) return true;

      if (
        !Object.prototype.hasOwnProperty.call(
          this.currentBusiness.metadata,
          "lockProductName"
        )
      ) {
        return true;
      }

      return !!this.currentBusiness.metadata.lockProductName;
    },

    totalQty() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.qty || 0);
      }, 0);
    },

    invoiceTotal() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.qty || 0) * Number(row.price || 0);
      }, 0);
    },

    selectCustomerError() {
      const errors = [];

      if (!this.$v.Order.CustomerId.$dirty) return errors;
      if (!this.$v.Order.CustomerId.required)
        errors.push("Please select customer");
      if (!this.$v.Order.CustomerId.minValue)
        errors.push("Please select customer");

      return errors;
    },
  },

  watch: {
    selectedCustomer(val) {
      if (!val || !val.id) return;

      this.Order.CustomerId = val.id;

      if (val.PriceTierId && !this.Order.PriceTierId) {
        this.Order.PriceTierId = val.PriceTierId;
      }
    },

    selectedUser(val) {
      if (val && val.id) {
        this.Order.SalesRepUserId = val.id;
      }
    },
  },

  created() {
    this.loadHeaders();
    this.getCustomerList();
    this.getProduct();
    this.getTiers();
    this.loadData();

    window.addEventListener("scroll", this.handleScroll, true);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll, true);
  },

  methods: {
    applySuggestedPrice(item) {
      if (!item || !item.metadata || !item.metadata.suggestedPrice) return;

      item.price = Number(Number(item.metadata.suggestedPrice).toFixed(2));
      item.metadata.priceChangedBySalesRep = true;
      item.metadata.priceSuggestionStatus = "pending";

      this.checkPrice(item);
      this.updateOrderPriceSuggestionMetadata();
      this.$forceUpdate();
    },
    shouldShowPriceSuggestionRow(item) {
      if (!item || !item.metadata) return false;
      if (
        item.metadata.originalPrice === null ||
        item.metadata.originalPrice === undefined
      )
        return false;

      const originalPrice = Number(item.metadata.originalPrice || 0);
      const currentPrice = Number(item.price || 0);
      const suggestedPrice = Number(item.metadata.suggestedPrice || 0);
      const status = item.metadata.priceSuggestionStatus;

      if (status === "restored") return suggestedPrice > 0;
      if (status === "pending") {
        return (
          Number(originalPrice.toFixed(2)) !== Number(currentPrice.toFixed(2))
        );
      }

      return false;
    },
    isPriceChangedBySalesRep(item) {
      return !!(item && item.metadata && item.metadata.priceChangedBySalesRep);
    },

    getOriginalPrice(item) {
      if (!item || !item.metadata) return null;

      const originalPrice = item.metadata.originalPrice;

      if (originalPrice === null || originalPrice === undefined) return null;

      return Number(originalPrice);
    },

    getSuggestedBy(item) {
      if (!item || !item.metadata) return null;

      return item.metadata.suggestedByUserName || null;
    },

    getItemImageUrl(item) {
      if (!item) return null;

      return (
        item.imageUrl ||
        item.thumbnailUrl ||
        (item.Product &&
          (item.Product.thumbnailUrl || item.Product.imageUrl)) ||
        null
      );
    },

    restoreOriginalPrice(item) {
      const originalPrice = this.getOriginalPrice(item);

      if (originalPrice === null) return;

      const suggestedPrice = Number(item.price || 0);

      if (item.metadata) {
        item.metadata.suggestedPrice = suggestedPrice;
        item.metadata.priceChangedBySalesRep = false;
        item.metadata.priceSuggestionStatus = "restored";
      }

      item.price = Number(originalPrice.toFixed(2));
      this.checkPrice(item);

      if (item.metadata) {
        item.metadata.suggestedPrice = suggestedPrice;
        item.metadata.priceSuggestionStatus = "restored";
        item.metadata.priceChangedBySalesRep = false;
      }

      this.updateOrderPriceSuggestionMetadata();
      this.$forceUpdate();
    },

    updateOrderPriceSuggestionMetadata() {
      const changedItems = this.Order.OrderDetails.filter((row) => {
        return row.metadata && row.metadata.priceChangedBySalesRep;
      });

      if (!this.Order.metadata) {
        this.$set(this.Order, "metadata", {});
      }

      if (changedItems.length) {
        this.$set(this.Order.metadata, "hasSalesRepPriceSuggestions", true);
        this.$set(
          this.Order.metadata,
          "salesRepPriceSuggestionCount",
          changedItems.length
        );
      } else {
        this.$delete(this.Order.metadata, "hasSalesRepPriceSuggestions");
        this.$delete(this.Order.metadata, "salesRepPriceSuggestionCount");
      }
    },
    handleScroll() {
      this.showFloatingBar = window.scrollY > 120;
    },

    loadHeaders() {
      this.headers = [
        {
          text: "",
          align: "center",
          value: "image",
          width: 60,
          sortable: false,
        },
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: false,
          width: "350px",
        },
      ];

      if (
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.boxCountOnInventory
      ) {
        this.headers.push({
          text: "Unit Count",
          align: "center",
          value: "unitCountInBox",
          sortable: false,
        });
      }

      this.headers.push(
        {
          text: "Qty",
          align: "center",
          value: "qty",
          width: "100",
          sortable: false,
        },
        {
          text: "Price",
          align: "right",
          value: "price",
          width: "150px",
          sortable: false,
        },
        {
          text: "Total",
          align: "right",
          width: "150px",
          value: "total",
          sortable: false,
        }
      );
    },

    cancel() {
      this.$router.go(-1);
    },

    checkPrice(item) {
      item.qty = Number(item.qty || 0);
      item.price = Number(item.price || 0);
      item.total = Number((item.qty * item.price).toFixed(2));

      if (item.metadata && item.metadata.originalPrice != null) {
        const originalPrice = Number(item.metadata.originalPrice || 0);
        const changed =
          Number(item.price.toFixed(2)) !== Number(originalPrice.toFixed(2));

        item.metadata.priceChangedBySalesRep = changed;
        item.metadata.suggestedPrice = changed ? item.price : null;
        item.metadata.priceSuggestionStatus = changed ? "pending" : "restored";
      }

      this.updateOrderPriceSuggestionMetadata();
      this.$forceUpdate();
    },

    async getTierById(id) {
      try {
        if (!id) return null;

        const response = await priceTierService.getById(id);
        return response.data;
      } catch (error) {
        console.error("Error fetching tier by ID:", error);
        return null;
      }
    },

    calculateTierPrice(originalPrice, selectedTier, productId) {
      if (!selectedTier) return Number(originalPrice || 0);

      let newPrice = Number(originalPrice || 0);

      if (selectedTier.discountType === "percentage") {
        const rate = Number(selectedTier.rate || 0);
        newPrice = newPrice - newPrice * (rate / 100);
      } else if (selectedTier.discountType === "fixed") {
        const productTier = Array.isArray(selectedTier.ProductPrices)
          ? selectedTier.ProductPrices.find(
              (p) => Number(p.ProductId) === Number(productId)
            )
          : null;

        if (productTier) {
          newPrice = Number(productTier.price || 0);
        }
      }

      return Number(newPrice.toFixed(2));
    },

    async onPriceTierChange() {
      const selectedTier = await this.getTierById(this.Order.PriceTierId);

      if (!Array.isArray(this.Order.OrderDetails)) return;

      this.Order.OrderDetails.forEach((detail) => {
        let basePrice = detail.price;

        if (detail.Product && detail.Product.price != null) {
          basePrice = detail.Product.price;
        }

        detail.price = this.calculateTierPrice(
          basePrice,
          selectedTier,
          detail.ProductId
        );

        detail.total = Number(
          (Number(detail.price || 0) * Number(detail.qty || 0)).toFixed(2)
        );
      });

      this.$forceUpdate();
    },

    async getTiers() {
      try {
        const response = await priceTierService.getAll();
        this.tiers = response.data || [];
      } catch (e) {
        console.error("Failed to load tiers", e);
        this.tiers = [];
      }
    },

    loadData() {
      const { id } = this.$route.params;

      return orderService.getById(id).then((response) => {
        this.Order = response.data;

        if (!this.Order.metadata) {
          this.Order.metadata = {};
        }

        this.initValue = this.Order.Customer || this.Order.CustomerId;
        this.selectedCustomer = this.Order.Customer || null;

        this.initUser = this.Order.SalesRep;
        this.selectedUser = this.Order.SalesRep;

        this.Order.OrderDetails = (this.Order.OrderDetails || []).map((row) => {
          return {
            ...row,
            imageUrl:
              row.imageUrl || (row.Product ? row.Product.imageUrl : null),
            total: Number(
              (Number(row.qty || 0) * Number(row.price || 0)).toFixed(2)
            ),
          };
        });

        this.loading = false;
        return response;
      });
    },

    saveOrder() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      if (!this.Order.OrderDetails.length) return;

      const payload = {
        ...this.Order,
        metadata: {
          ...(this.Order.metadata || {}),
          updatedFrom: "inHouse",
          updatedFromAt: this.$moment().toISOString(),
        },
      };

      return orderService.update(this.Order.id, payload).then(() => {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Order was updated!",
          });

        this.$router.go(-1);
      });
    },

    submitOrder() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      if (!this.Order.OrderDetails.length) return;

      this.$swal({
        title: "Submit draft order?",
        text: "This draft will become a submitted order.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Cancel",
      }).then(async (result) => {
        if (!result.value) return;

        const payload = {
          ...this.Order,
          metadata: {
            ...(this.Order.metadata || {}),
            updatedFrom: "inHouse",
            updatedFromAt: this.$moment().toISOString(),
          },
        };

        await orderService.update(this.Order.id, payload);
        await orderService.submitDraft(this.Order.id);

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Order was submitted!",
          });

        this.$router.go(-1);
      });
    },

    deleteItem(index) {
      const item = this.Order.OrderDetails[index];

      this.$swal({
        title: "Remove item?",
        text: `Remove ${
          item && item.name ? item.name : "this item"
        } from the order?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value) {
          this.Order.OrderDetails.splice(index, 1);
        }
      });
    },

    addRow(val) {
      const product = val.Bar ? val.Bar : val;

      const qty = Number(product.qty || val.qty || 1);
      const price = Number(product.price || val.price || 0);

      this.Order.OrderDetails.push({
        ProductId: val.id,
        Product: val,
        name: val.name || product.name,
        price,
        total: Number((price * qty).toFixed(2)),
        qty,
        unitCountInBox: val.unitCountInBox || null,
        imageUrl: val.imageUrl || null,
      });
    },

    getProduct() {
      return productService.getAll().then((result) => {
        this.Products = result.data || [];
      });
    },

    getCustomerList() {
      this.loading = true;

      return customerService.getAll().then((response) => {
        this.Customer = response.data || [];

        if (this.$route.query && this.$route.query.CustomerId) {
          const customerId = this.$route.query.CustomerId;
          this.selectedCustomer = this.Customer.find(
            ({ id }) => Number(id) === Number(customerId)
          );
        }

        this.loading = false;
        return response;
      });
    },
  },
};
</script>

<style scoped>
.order-create-page {
  background: #f8fafc;
  min-height: 100%;
}

.floating-order-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0 0 18px 18px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.14);
  padding: 10px 14px;
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.floating-order-bar.show {
  display: flex;
}

.floating-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-create-card,
.order-cart-card,
.start-card {
  border-radius: 20px;
  background: #ffffff;
}

.order-create-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.order-create-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.order-create-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 3px;
}

.order-create-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.field-label {
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  margin-bottom: 6px;
}

.price-change-row td {
  padding: 0 14px 10px 14px !important;
  border-bottom: 1px solid #e2e8f0;
}

.price-change-edit-alert {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}

.price-change-edit-alert.restored {
  background: #f0fdf4;
  border-color: #86efac;
  color: #166534;
}

.price-change-content {
  flex: 1;
}

.note-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.note-preview {
  color: #64748b;
  font-size: 13px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 7px 10px;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.dialog-actions {
  padding: 12px 16px;
}

.cart-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.cart-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
}

.cart-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.cart-total {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
}

.order-cart-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.image-cell {
  width: 60px;
  text-align: center;
}

.cart-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.qty-cell {
  width: 110px;
}

.price-cell {
  width: 150px;
}

.centered-input input {
  text-align: center;
}

.al-r input {
  text-align: right;
}
.cart-image-avatar {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.price-change-edit-alert {
  margin-top: 6px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  border-radius: 10px;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 3px;
}

@media (max-width: 600px) {
  .order-create-header,
  .cart-header {
    align-items: stretch;
    flex-direction: column;
  }

  .order-create-actions {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .order-create-actions .v-btn {
    width: 100%;
  }

  .cart-total {
    font-size: 18px;
  }
}
</style>
